import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ToggleSwitch from "../ToggleSwitch";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: "100%",
  },
  sectionCard: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)"}`,
    marginBottom: theme.spacing(2.5),
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "10px 18px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f1f5f9",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: theme.palette.primary.main,
  },
  sectionBody: {
    padding: "16px 18px",
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: 6,
  },
  tableHead: {
    "& .MuiTableCell-head": {
      fontWeight: 700,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: theme.palette.text.secondary,
      padding: "8px 12px",
    },
  },
  tableCell: {
    padding: "10px 12px",
  },
  emptyRow: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 13,
    padding: theme.spacing(3),
  },
  addBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
  },
  dialogField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
}));

const FinalizacaoAtendimento = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [informarValorVenda, setInformarValorVenda] = useState(false);

  const t = (key) => i18n.t(`settings.finalizacaoAtendimento.${key}`);

  useEffect(() => {
    if (settings) setInformarValorVenda(settings.informarValorVenda || false);
    fetchMotivos();
  }, [settings]);

  const fetchMotivos = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivos(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenModal = (motivo = null) => {
    if (motivo) {
      setEditingMotivo(motivo);
      setFormData({ name: motivo.name, description: motivo.description || "" });
    } else {
      setEditingMotivo(null);
      setFormData({ name: "", description: "" });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMotivo(null);
    setFormData({ name: "", description: "" });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error(t("toasts.nameRequired"));
      return;
    }
    setLoading(true);
    try {
      if (editingMotivo) {
        await api.put(`/ticketFinalizationReasons/${editingMotivo.id}`, formData);
        toast.success(t("toasts.updated"));
      } else {
        await api.post("/ticketFinalizationReasons", formData);
        toast.success(t("toasts.created"));
      }
      handleCloseModal();
      fetchMotivos();
    } catch (err) {
      toast.error(err.response?.data?.error || t("toasts.errorSave"));
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("toasts.deleteConfirm"))) {
      try {
        await api.delete(`/ticketFinalizationReasons/${id}`);
        toast.success(t("toasts.deleted"));
        fetchMotivos();
      } catch (err) {
        toast.error(err.response?.data?.error || t("toasts.errorDelete"));
      }
    }
  };

  const handleInformarValorVendaChange = async (val) => {
    setInformarValorVenda(val);
    try {
      await api.put("/companySettings", { column: "informarValorVenda", data: val });
      if (onSettingsChange) onSettingsChange({ ...settings, informarValorVenda: val });
      toast.success(t("toasts.settingUpdated"));
    } catch (err) {
      toast.error("Error al actualizar la configuración");
      setInformarValorVenda(!val);
    }
  };

  return (
    <div className={classes.root}>

      {/* Configuración general */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            {t("generalSettings")}
          </Typography>
        </div>
        <div className={classes.sectionBody}>
          <ToggleSwitch
            checked={informarValorVenda}
            onChange={handleInformarValorVendaChange}
            label={t("informSaleValue")}
          />
          <Typography className={classes.helperText}>
            {t("informSaleValueHelper")}
          </Typography>
        </div>
      </div>

      {/* Motivos de cierre */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            {t("closingReasons")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            className={classes.addBtn}
          >
            {t("addReason")}
          </Button>
        </div>
        <div className={classes.sectionBody} style={{ padding: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableCell}>{t("table.name")}</TableCell>
                  <TableCell className={classes.tableCell}>{t("table.description")}</TableCell>
                  <TableCell className={classes.tableCell} align="right" style={{ width: 100 }}>
                    {t("table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {motivos.map((motivo) => (
                  <TableRow key={motivo.id} hover>
                    <TableCell className={classes.tableCell}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {motivo.name}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography variant="body2" style={{ color: "var(--text-secondary)" }}>
                        {motivo.description || t("table.noDescription")}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      <IconButton size="small" onClick={() => handleOpenModal(motivo)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(motivo.id)} color="secondary">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {motivos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className={classes.emptyRow}>
                      {t("noReasons")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Modal agregar/editar motivo */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMotivo ? t("modal.editTitle") : t("modal.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("modal.nameLabel")}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                variant="outlined"
                margin="dense"
                required
                autoFocus
                className={classes.dialogField}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("modal.descriptionLabel")}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
                className={classes.dialogField}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: "8px 16px 16px" }}>
          <Button onClick={handleCloseModal} disabled={loading} variant="outlined" style={{ borderRadius: 8, textTransform: "none" }}>
            {t("buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}
          >
            {loading
              ? t("buttons.saving")
              : editingMotivo
              ? t("buttons.update")
              : t("buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinalizacaoAtendimento;
